import { CONST as BET_ENGINE_CONSTS } from "@ppb/bet-engine";
import { getExchangeRunnerTree } from "../store/state/exchange-markets/exchange-markets-selectors";
import { getBetslipExchangeContext } from "../store/state/betslip-module/betslip-module-selectors";
import { BetslipApplicationState } from "../store/state/BetslipApplicationState";
import { formatRunnerNameWithLine } from "./handicap-line";

/**
 * Builds the inline exchange betslip title by joining the market name and runner name with " - ".
 *
 * Resolves the runner URN from `betslipModule.exchangeContext`, looks up the corresponding runner
 * tree, and reads both the market name and the runner name off it, then returns a title such as
 * `"Match Odds - Team A"`. On a market bet one line at a time the runner name carries its line
 * (`"Asian Handicap - Arsenal +0.5"`), so the customer can tell which line they are betting on.
 * Returns an empty string when neither name is available.
 *
 * @param appState - The full application state.
 * @returns A formatted title in the form "MarketName - RunnerName", or a partial/empty string
 *          if either piece of data is missing.
 */
export const getInlineBetslipTitle = (appState: BetslipApplicationState): string => {
  const runnerUrn = getBetslipExchangeContext(appState)?.runner ?? "";
  const runnerTree = getExchangeRunnerTree(appState, runnerUrn);

  const marketRunner = runnerTree?.market.runners.find((item) => item.urn === runnerUrn);
  const runnerName =
    marketRunner && runnerTree && formatRunnerNameWithLine(marketRunner.name, marketRunner.handicap, runnerTree.market);
  const marketName = runnerTree?.market.name;

  return [marketName, runnerName].filter(Boolean).join(" - ");
};

/**
 * Returns `true` when the given market type is an Each Way market.
 *
 * @param marketType - The market type string, as supplied by the BFF.
 * @returns `true` if the market is of type EACH_WAY, `false` otherwise.
 */
export const isEachWayMarket = (marketType: string): boolean => marketType === BET_ENGINE_CONSTS.MARKET.TYPE.EACH_WAY;

export const isLineBettingType = (bettingType?: string): boolean => bettingType === "LINE";
